
import {Component, Watch} from "vue-property-decorator";
import {Getter} from "vuex-class";
import MobileListKeyItem from "@/components/mobile/MobileListKeyItem.vue";
import KeyTransit from "@/components/keys/actions/KeyTransit.vue";
import KeyRetrieve from "@/components/keys/actions/KeyRetrieve.vue";
import HandToHand from "@/components/keys/actions/HandToHand.vue";
import KeyWithdraw from "@/components/keys/actions/KeyWithdraw.vue";
import KeyWithdrawConnected from "@/components/keys/actions/KeyWithdrawConnected.vue";
import KeyDropAgency from "@/components/keys/actions/KeyDropAgency.vue";
import KeyDropConnected from "@/components/keys/actions/KeyDropConnected.vue";
import { removeVuetifyFileInputBug, concatListElem } from "@/utils/misc";
import KeyCarousel from "@/components/keys/KeyCarousel.vue";
import {dataPicture} from "@/utils/pictureFonction";
import KeyArchive from "@/components/keys/actions/KeyArchive.vue";
import RelayGiveKeyToGuest from "@/components/relays/RelayGiveKeyToGuest.vue";
import { CONTEXT_TYPES, IContext } from "@/store/modules/context";
import ReserveLocation from "@/components/keys/ReserveLocation.vue";
import { mixins } from "vue-class-component";
import DocumentMixin from "@/mixins/DocumentMixin.vue";
import {
  mdiQrcodeEdit,
  mdiQrcodePlus,
  mdiAccountKey,
  mdiKey,
  mdiStore,
  mdiAccountCog, mdiFormatListBulletedType,
} from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";
import KeyEdit from "@/components/keys/KeyEdit.vue";
import RelayBookedKey from "@/components/relays/RelayBookedKey.vue";
import router from "@/router";

@Component({
    methods: { concatListElem },
    components: {
        KeyEdit,
        KeyCarousel,
        MobileListKeyItem,
        KeyTransit,
        KeyRetrieve,
        KeyDropAgency,
        KeyDropConnected,
        KeyWithdraw,
        KeyWithdrawConnected,
        KeyArchive,
        HandToHand,
        RelayGiveKeyToGuest,
        RelayBookedKey,
        ReserveLocation,
        SvgIcon,
    },
})
export default class KeyMobile extends mixins(DocumentMixin) {

    get isKeyArchived(): boolean {
        return this.key?.archivedAt;
    }

    get getCurrentUserHandToHandTransaction(): any|undefined {
        return this.key?.HandToHand?.find((handToHand) => handToHand?.targetUserId === this.currentUser?.id);
    }

    get archivedText(): string {
        if (!this.hasRight("UNARCHIVE_KEY")) {
            return "key.archivedGuest";
        } else {
            return "key.archived";
        }
    }

    get isCompanyBlocked(): boolean {
        return this.key?.company && !!this.key?.company?.blockedAt;
    }

    get keyReferenceRelay(): any {
        return this.key?.Relays?.find((relay) => relay.type === "AGENCY") || this.key?.Relays?.[0];
    }

    // check if one of the user of one of the management groups match the current user
    get isManager(): boolean {
      return !!this.key?.legitimateKeyAccess?.keyManagementId;
    }

    get currentLocation() {
        return this.key?.KeyLocation?.find((kL) => !kL.endDate)?.Location;
    }

    get isKeyInConnectedBox(): boolean {
        return !!this.currentLocation?.Box?.ConnectedBox;
    }

    get getKeyParentIfExists(): string {
        return this.key.Parent?.name ? `${this.key.Parent?.name} | ` : "";
    }

    get displayReserveLocationButton(): boolean {
      return this.locationLock && this.hasRight("EDIT_KEY_RESERVED_LOCATION");
    }

    get hasArchiveRight(): boolean {
        return this.hasRight("ARCHIVE_KEY") || this.hasRight("UNARCHIVE_KEY");
    }

    @Getter private currentUser: any;
    @Getter private canDoInEnvCompany: (right: string) => boolean;
    @Getter private context: IContext|null;
    @Getter private isBookedKeyAvailable: (key: any) => boolean;

    private key: any = null;
    private keyId: number = null;
    private isLoadingKeyActions: boolean = true;
    private loadingGetKey: boolean = true;
    private editKeyInfoDialog: boolean = false;
    private avatar: any = null;
    private showCarouselDialog: boolean = false;
    private keyPictures: any[] = [];
    private displayKeyActions = true;
    private componentKey: number = 0;
    private heicFile: any = null;
    private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;
    private displayReserveLocationModal: boolean = false;
    private locationLock: boolean = false;
    private subTab: string = null;
    private loadingSubTab: boolean = false;

    private icons = {
      // Sub-tabs
      mdiAccountKey, // Guests
      mdiAccountCog, // Managers
      mdiKey, // Sub-keys
      mdiStore, // Relay
      // Logs ?
      mdiFormatListBulletedType, // Logs
      // Reserve location flow
      mdiQrcodeEdit,
      mdiQrcodePlus,
    };

    @Watch("key", { immediate: true, deep: true })
    public onCurrentKeyChanged(newValue: any, oldValue: any) {
      if ((!oldValue || newValue !== oldValue) && this.keyReferenceRelay) {
        this.fetchRelayOptions();
      }
    }

    private async fetchRelayOptions(): Promise<void> {
      // Check if location can be locked in either relay or building
      this.locationLock = await Promise.all([
        // Reference relay
        this.$store.dispatch("options/readOptionValueByBuilding", {
          buildingId: this.keyReferenceRelay.buildingId || this.keyReferenceRelay.Building.id,
          optionTypeLabel: "locationLock",
        }),
        // Linked building
        this.key.buildingId ? this.$store.dispatch("options/readOptionValueByBuilding", {
          buildingId: this.key.buildingId,
          optionTypeLabel: "locationLock",
        }) : false,
      ]).then((res) => res.some(Boolean));
    }

    private checkDisplayKeyActions(): void {
        this.isLoadingKeyActions = false;
        const actionsContainer = document.getElementById("key-actions");
        this.displayKeyActions = actionsContainer?.children?.length > 1;
    }

    private handlerSavePicture( newPicture: any) {
        this.avatar = newPicture.avatar;
        this.heicFile = newPicture.heicFile;
        const profile = false;
        const key = {id: this.keyId, name: this.key.name};
        this.savePicture(key, newPicture, profile);
    }

    private mounted() {
        this.keyId = Number(this.$route.params.id);
        this.reset();
    }

    private beforeDestroy(): void {
      // If the context is location based, remove it
      if (this.context?.type === CONTEXT_TYPES.LOCATION) {
        this.clearContext();
      }
    }

    private clearContext() {
        this.$store.dispatch("context/clearContext");
    }

    private getKeyById() {
        this.loadingGetKey = true;
        return this.$store.dispatch("keys/getById", {
            id: this.keyId,
            options: {
                picture: true,
                relays: true,
                reservedLocation: true,
                holder: true,
                currentLocation: true,
                company: true,
            },
        }).then(async (key) => {

            // Update global env companyId with current entity company
            await this.$store.dispatch("changeEnvCompanyId", key.companyId);

            key.isFollowed = await this.$store.dispatch("keys/getIsFollowedKey", {
              keyId: this.keyId,
              userId: this.currentUser.id,
            });
            key.relayId = key?.Relays?.filter((r) => r.type === "AGENCY")?.[0]?.id;
            key.Relays = key?.Relays?.map((relay) => ({
                value: relay.id,
                ...relay,
            }));
            key.newTags = key.Tags;
            key.transitMode = "";
            key.reservedLocation = key?.ReservedLocations?.[0];
            key.locationDisplayed = key?.currentLocation || key?.ReservedLocations?.[0];
            key.newGuests = [];
            this.key = key;
        }).catch((error) => {
          if (error.response?.data?.error === "Unauthorized") {
            router.push({name: "keys"});
            this.$store.commit("alerts/displayError", {
              msg: this.$i18n.t("alerts.error.Unauthorized"),
            });
          } else {
            this.$store.commit("alerts/displayHttpError", { error });
          }
        }).finally(() => {
            this.$nextTick().then(() => {
                this.checkDisplayKeyActions();
            });
            this.loadingGetKey = false;
        });
    }

    private openEditKeyInfoModal(): void {
        this.editKeyInfoDialog = true;
    }

    private goHandToHandScan(): void {
        this.$router.push({
            name: "key-hand-to-hand-scan",
            params: {
                keyId: this.key.id,
                token: this.getCurrentUserHandToHandTransaction?.token,
            },
        });
    }

    private updateData() {
        if (!this.isManager) {
            this.$router.push({name: "keys"});
        } else {
            this.getKeyById();
        }
    }

    private beforeUpdate(): void {
        this.removeVuetifyFileInputBug(document);
    }

    private reset() {
        this.editKeyInfoDialog = false;
        this.getKeyById();
        this.forceImageKeyRender();
    }

    private async getKeyPictures(): Promise<void> {
        await this.$store.dispatch("keys/getAllPictures", {
            id: this.key.id,
        }).then((res) => {
            this.keyPictures = res.pictures;
            this.showCarouselDialog = true;
        }).catch((error) => {
          this.$store.commit("alerts/displayHttpError", { error });
        });
    }

    private async savePicture(key , imgName: any , profile) {
        const data = await dataPicture(
            key,
            imgName,
            profile,
            this.currentUser,
            this.heicFile,
            this.avatar,
        );
        const saveImageRes = await this.$store.dispatch("files/uploadKeysPicture",
            {formData: data.formData, keyId : key.id},
          );
        data.picture.name = saveImageRes.name;
        data.picture.picturePath = data.picture.picturePathFn(saveImageRes.name);
        return this.$store.dispatch("keys/addPicture", {
            picture: data.picture,
            keyId: key.id,
        }).then(() => {
            this.$store.commit("alerts/displaySuccess", { icon: "icon-picto_cle-partagees", msg: this.$t("keyPicture.add")});
            this.getKeyById();
        });
    }

    private closeEditKeyInfoDialog(): void {
        this.editKeyInfoDialog = false;
        this.key.newTags = this.key.Tags;
    }

    private forceImageKeyRender() {
        this.componentKey += 1;
    }

    private hasRight(right: string): boolean {
        return this.canDoInEnvCompany(right);
    }

  private hasCategoryRight(category: string): boolean {
    if (category === "actionsLikeManager") {
      const rights = [
        "DROP_KEY",
        "WITHDRAW_KEY",
        "RETRIEVE_KEY",
        "ARCHIVE_KEY",
        "UNARCHIVE_KEY",
      ];
      return rights.some((right) => this.canDoInEnvCompany(right));
    } else if (category === "editKey") {
      const editRights = [
        "EDIT_KEY_NAME",
        "EDIT_KEY_DESCRIPTION",
        "EDIT_KEY_TAGS",
        "EDIT_KEY_IMAGE",
        "EDIT_KEY_PRIVATE_IMAGES",
        "EDIT_KEY_RELAY",
        "EDIT_KEY_RESERVED_LOCATION",
      ];
      return editRights.some((right) => this.canDoInEnvCompany(right));
    }
  }

    private displayInvitationMessage(data: {
    endDate: Date,
    startDate: Date,
    countAccessLeft: number,
    }) {
        const invitationType = data.countAccessLeft === -1 ? this.$t("invitation.unlimited")
        : this.$t("invitation.punctual");
        const plageHoraires = data.endDate ? this.$t("invitation.periodWithBeginAndEnd",
            {
            startDate: this.$options.filters.formatDate(data.startDate),
            endDate: this.$options.filters.formatDate(new Date(data.endDate)),
            }) : "";
        return  this.$t("invitation.notification", {
        invitationType,
        plageHoraires,
        });
    }
}
